import { CASCADE_ENUM } from '../../../../services/enum.service';
import { VPPA_RFQ_STATUS } from '../../../../utils';
import {
  HyperlinkStaticCellRenderer,
  IGridHyperlink,
} from '../../../../utils/custom-renderer/hyperlink-cell-renderer';
import {
  formatMMDDYYYY,
  getNumericPattern,
  getStatusTranslate,
  getFormattedNumber,
} from '../../../../utils/formatting';
import { getAbbreviationForCreditProfile } from '../../../../utils/credit-profiles';
import { CUSTOM_HEADER_TOOLTIPS, headerWithTooltipColParams } from '../../../../utils/grid';
import { CreditProfileLegendTooltipComponent } from '../../../../utils/custom-renderer/credit-profile-legend-tooltip-component';

export enum COLUMN_FIELDS {
  TICKER = 'TICKER',
  UNIQUE_ID = 'UNIQUE_ID',
  TECHNOLOGY = 'TECHNOLOGY',
  NAME = 'NAME',
  ISO = 'ISO',
  SETTLEMENT_POINT = 'SETTLEMENT_POINT',
  VOLUME = 'OFFERED_CAPACITY',
  FIXED_PRICE = 'FIXED_PRICE',
  TENOR_START_DATE = 'TENOR_START_DATE',
  TENOR_MONTHS = 'TENOR_MONTHS',
  SELLER_POST_COD_COLLATERAL = 'SELLER_POST_COD_COLLATERAL',
  IMPLIED_CARBON_PRICE = 'IMPLIED_CARBON_PRICE',
  VPPA_STATUS = 'VPPA_STATUS',
  SELECTION = '',
}

function getRfqStyle(params: any) {
  if (params.data?.RFQ_ID) {
    return { 'background-color': 'black' };
  }
  return '';
}

function getStatusOwnCompanyStyle(params: any) {
  const response = {};
  if (params.data?.RFQ_ID) {
    response['background-color'] = 'black';
    response['color'] = '#7acc79';
    return response;
  }

  if (params.data?.VPPA_STATUS === VPPA_RFQ_STATUS.ACTIVE) {
    response['color'] = '#7acc79';
  }

  if (params.data?.VPPA_STATUS === VPPA_RFQ_STATUS.EXISTING_INTEREST) {
    response['color'] = 'rgba(255, 182, 96, 0.99)';
  }

  return response;
}

export function MARKET_DEPTH_VPPA_COLUMNS(context: any) {
  return [
    {
      field: COLUMN_FIELDS.SELECTION,
      headerName: '',
      suppressMenu: true,
      flex: 1,
      cellStyle: (params) => getRfqStyle(params),
      checkboxSelection: (params) => {
        if (params.data.RFQ_ID) {
          return false;
        }

        return true;
      },
    },
    {
      field: COLUMN_FIELDS.TICKER,
      headerName: 'Ticker',
      suppressMenu: true,
      flex: 1,
      cellStyle: (params) => getRfqStyle(params),
    },
    {
      field: COLUMN_FIELDS.UNIQUE_ID,
      headerName: 'Unique ID',
      suppressMenu: true,
      flex: 1,
      cellStyle: (params) => getRfqStyle(params),
    },
    {
      field: COLUMN_FIELDS.TECHNOLOGY,
      headerName: 'Technology',
      suppressMenu: true,
      flex: 1,
      cellStyle: (params) => getRfqStyle(params),
    },
    {
      field: COLUMN_FIELDS.NAME,
      headerName: 'Project',
      suppressMenu: true,
      flex: 1,
      cellStyle: (params) => getRfqStyle(params),
      cellRenderer: HyperlinkStaticCellRenderer,
      cellRendererParams: (params) => {
        if (!params.data.VPPA_ID) {
          const hyperlinkParams: IGridHyperlink = {
            value: '---',
            id: null,
            vppaId: null,
            iconList: [],
          };
          return hyperlinkParams;
        }

        const hyperlinkParams: IGridHyperlink = {
          value: params.value,
          id: params.data.PROJECT_ID,
          vppaId: null,
          iconList: [],
        };
        return hyperlinkParams;
      },
    },
    {
      field: COLUMN_FIELDS.ISO,
      headerName: 'ISO',
      suppressMenu: true,
      flex: 1,
      cellStyle: (params) => getRfqStyle(params),
    },
    {
      field: COLUMN_FIELDS.SETTLEMENT_POINT,
      headerName: 'Settlement Point',
      suppressMenu: true,
      flex: 1,
      cellStyle: (params) => getRfqStyle(params),
      valueFormatter: (params) => {
        if (params.data.RFQ_ID) {
          if (params.data.SETTLEMENT_POINT) {
            return params.data.SETTLEMENT_POINT;
          } else {
            return '[Not Specified]';
          }
        }
      },
    },
    {
      field: COLUMN_FIELDS.VOLUME,
      headerName: 'Volume',
      suppressMenu: true,
      flex: 1,
      cellStyle: (params) => getRfqStyle(params),
      valueFormatter: (params) => getNumericPattern(params.data.OFFERED_CAPACITY),
    },
    {
      field: COLUMN_FIELDS.FIXED_PRICE,
      headerName: 'Fixed Price Range',
      suppressMenu: true,
      cellStyle: (params) => getRfqStyle(params),
      valueFormatter: (params) => getNumericPattern(params.data.FIXED_PRICE, null, '$'),
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.TENOR_START_DATE,
      headerName: 'Start Date',
      suppressMenu: true,
      cellStyle: (params) => getRfqStyle(params),
      valueFormatter: (params) => formatMMDDYYYY(params.data.TENOR_START_DATE),
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.TENOR_MONTHS,
      headerName: 'Tenor (mo)',
      suppressMenu: true,
      cellStyle: (params) => getRfqStyle(params),
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.SELLER_POST_COD_COLLATERAL,
      headerName: 'Credit Profile ($/MW)',
      suppressMenu: true,
      cellStyle: (params) => getRfqStyle(params),
      valueFormatter: (params) => {
        return getNumericPattern(
          params.data.SELLER_POST_COD_COLLATERAL,
          getAbbreviationForCreditProfile(params.data.VPPA_CREDIT_PROFILE),
          '$',
        );
      },
      flex: 1,
      ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.CREDIT_PROFILE),
    },
    {
      field: COLUMN_FIELDS.IMPLIED_CARBON_PRICE,
      headerName: 'Implied Carbon Price Range',
      suppressMenu: true,
      cellStyle: (params) => getRfqStyle(params),
      valueFormatter: (params) => getFormattedNumber(params.data.IMPLIED_CARBON_PRICE, '', 2, '$'),
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.VPPA_STATUS,
      headerName: 'Status',
      suppressMenu: true,
      cellStyle: (params) => getStatusOwnCompanyStyle(params),
      valueFormatter: (params) => {
        if (params.data.RFQ_ID) {
          return '---';
        } else {
          return getStatusTranslate(params.data.VPPA_STATUS);
        }
      },
      flex: 1,
    },
  ];
}
